import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { sendToApi } from 'api/client'
import { ensureUploadFile } from 'utils/ensureUploadFilename'

export interface Data {
  url: string
}

export type Options = Omit<UseMutationOptions<Data, Error, Variables>, 'mutationKey' | 'mutationFn'>

interface Props {
  workspaceId: number
}

type Variables = {
  file: File
  folderId?: string
  onProgress?: (progress: number) => void
}

const generatePresignedUrl = async (workspaceId: number, filename: string) => {
  return sendToApi<{ presigned_url: string }>(`/w/${workspaceId}/media_objects/presigned_urls`, {
    method: 'POST',
    body: {
      filename,
    },
  })
}

export const uploadFile = async ({ workspaceId, folderId, file, onProgress }: Props & Variables) => {
  const uploadFilePayload = ensureUploadFile(file)
  const signedUrlRes = !!folderId
    ? await MediaFileClient.getSignedUrl(workspaceId, folderId, uploadFilePayload.name)
    : await generatePresignedUrl(workspaceId, uploadFilePayload.name)

  if (signedUrlRes.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to sign file', {
      metaData: {
        workspaceId,
        folderId,
        file,
        response: signedUrlRes.data,
      },
    })
    throw new Error(
      buildErrorMessage(signedUrlRes.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }

  const url = !!folderId
    ? (signedUrlRes.data as { pre_signed_url: string }).pre_signed_url
    : (signedUrlRes.data as { presigned_url: string }).presigned_url

  const uploadToS3Response = await MediaFileClient.uploadToS3(
    url,
    uploadFilePayload,
    (progress) => onProgress?.(progress),
    workspaceId,
  )

  return { url: uploadToS3Response.url.split('?')[0] }
}

export const useUploadFileToS3 = (options?: Options) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>(
    ['workspace', currentWorkspaceId, 'uploadFile'],
    async (variables) => {
      return uploadFile({ workspaceId: currentWorkspaceId, ...variables })
    },
    {
      ...options,
    },
  )
}
